import type { ColDef, ICellRendererParams, ValueGetterParams, GetQuickFilterTextParams } from 'ag-grid-community'
import { Texto, Vertical, addCommasToNumber } from '@gravitate-js/excalibrr'
import { Tooltip } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import moment from 'moment'
import { DocumentNumberRenderer } from './renderers/DocumentNumberRenderer'
import { ManageMatchActionRenderer } from './renderers/ManageMatchActionRenderer'
import { ProductsCellRenderer } from '../renderers/ProductsCellRenderer'
import { getUserTimezone, formatters } from '@utils/intl/dateTimeFormatters'
import { formatVolumeConsistent } from '../detailColumnDefs/formatters'
import type { MatchInvoice } from '@api/useSupplierReconciliation/types/match'
import type { ColumnPrecision } from '../../utils/precisionCalculator'
import {
  createDualColumnFilter,
  createDualColumnFilterParams,
  createDateStatusDualColumnFilter,
  createDateStatusDualColumnFilterParams,
} from '../../../components/columnDefs/utils/filterUtils'
import { useFormatter } from '@api/useFormatter'

dayjs.extend(utc)
dayjs.extend(timezone)

// Currency cell renderer components that use market profile currency symbol
function ProductTotalCurrencyRenderer({ value }: ICellRendererParams<MatchInvoice>) {
  const { formatCurrency } = useFormatter()
  const currencySymbol = formatCurrency(null, true)

  if (value == null || value === 0) {
    return (
      <Texto category='p2' appearance='hint'>
        —
      </Texto>
    )
  }

  const sign = value < 0 ? '-' : ''
  const formatted = `${sign}${currencySymbol}${addCommasToNumber(Math.abs(value), 2)}`
  return (
    <Texto category='p2' weight='500' align='right'>
      {formatted}
    </Texto>
  )
}

function TaxTotalCurrencyRenderer({ value }: ICellRendererParams<MatchInvoice>) {
  const { formatCurrency } = useFormatter()
  const currencySymbol = formatCurrency(null, true)

  if (value == null || value === 0) {
    return (
      <Texto category='p2' appearance='hint'>
        —
      </Texto>
    )
  }

  const sign = value < 0 ? '-' : ''
  const formatted = `${sign}${currencySymbol}${addCommasToNumber(Math.abs(value), 2)}`
  return (
    <Texto category='p2' align='right'>
      {formatted}
    </Texto>
  )
}

function InvoiceTotalCurrencyRenderer({ value }: ICellRendererParams<MatchInvoice>) {
  const { formatCurrency } = useFormatter()
  const currencySymbol = formatCurrency(null, true)

  const amount = value || 0
  const sign = amount < 0 ? '-' : ''
  const formatted = `${sign}${currencySymbol}${addCommasToNumber(Math.abs(amount), 2)}`
  return (
    <Texto category='p2' weight='600' align='right'>
      {formatted}
    </Texto>
  )
}

interface HelperFunctions {
  isItemMatched: (item: MatchInvoice) => boolean
  getLinkedNumbers: (item: MatchInvoice) => string[]
}

interface ActionHandlers {
  handleAddToMatch: (type: 'invoice' | 'bol', item: MatchInvoice, itemId: string, matchId?: string) => void
  handleRemoveFromMatch: (type: 'invoice' | 'bol', itemId: string, item?: MatchInvoice) => void
  pendingItems: MatchInvoice[]
}

export function createInvoiceColumns(
  helpers: HelperFunctions,
  actions: ActionHandlers,
  timezone?: string,
  precisions?: ColumnPrecision
): ColDef<MatchInvoice>[] {
  // Use provided precisions or calculate from data, fallback to defaults
  const columnPrecisions = precisions || { volume: 3, perUnit: 6, currency: 2 }
  return [
    {
      headerName: 'Invoice #',
      width: 160,
      pinned: 'left',
      suppressSizeToFit: true,
      filter: createDualColumnFilter({
        field: 'invoice_number',
        numberFieldTitle: 'Filter By Invoice Number',
        statusFieldTitle: 'Filter By Status',
        valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
          return params.data?.invoice_number || ''
        },
        statusValueGetter: (params: ValueGetterParams<MatchInvoice>) => {
          // Use helper function to determine match status
          return helpers.isItemMatched(params.data!) ? 'Matched' : 'Unmatched'
        },
      }),
      filterParams: createDualColumnFilterParams({
        field: 'invoice_number',
        numberFieldTitle: 'Filter By Invoice Number',
        statusFieldTitle: 'Filter By Status',
        valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
          return params.data?.invoice_number || ''
        },
        statusValueGetter: (params: ValueGetterParams<MatchInvoice>) => {
          return helpers.isItemMatched(params.data!) ? 'Matched' : 'Unmatched'
        },
      }),
      valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
        return params.data?.invoice_number || ''
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<MatchInvoice>) => {
        const number = params.data?.invoice_number || ''
        const status = helpers.isItemMatched(params.data!) ? 'Matched' : 'Unmatched'
        return `${number} ${status}`
      },
      cellRenderer: (params: ICellRendererParams<MatchInvoice>) => {
        return DocumentNumberRenderer({
          value: params.value,
          data: params.data,
          helpers,
        })
      },
    },
    {
      headerName: 'Invoice Date',
      width: 120,
      filter: createDateStatusDualColumnFilter({
        field: 'invoice_date',
        dateFieldTitle: 'Filter By Invoice Date',
        statusFieldTitle: 'Filter By Status',
        statusValueGetter: (params: ValueGetterParams<MatchInvoice>) => {
          const item = params.data
          const date = item?.invoice_date || item?.date
          if (!date) return 'Missing'
          return 'Has Date'
        },
      }),
      filterParams: createDateStatusDualColumnFilterParams({
        field: 'invoice_date',
        dateFieldTitle: 'Filter By Invoice Date',
        statusFieldTitle: 'Filter By Status',
        statusValueGetter: (params: ValueGetterParams<MatchInvoice>) => {
          const item = params.data
          const date = item?.invoice_date || item?.date
          if (!date) return 'Missing'
          return 'Has Date'
        },
      }),
      valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
        const item = params.data
        // Try to get invoice date from various possible fields
        // Based on API response structure - need to find the date field
        const date = item?.invoice_date || item?.date
        if (!date) return null
        // CRITICAL: Return the string directly, don't parse to Date here
        // Let the cellRenderer handle UTC parsing properly
        return date
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<MatchInvoice>) => {
        const item = params.data as any
        const date = item?.invoice_date || item?.date
        if (!date) return 'Missing'

        // Use timezone from match context or user timezone
        const formatted = formatters.DATE(moment.utc(date), timezone, false)
        return `${formatted || dayjs.utc(date).tz(getUserTimezone()).format('MMM DD, YYYY')} Has Date`
      },
      cellRenderer: (params: ICellRendererParams<MatchInvoice>) => {
        const date = params.value
        if (!date)
          return (
            <Texto category='p2' appearance='hint'>
              —
            </Texto>
          )

        // Parse as UTC (moment.utc handles both Z and non-Z timestamps correctly)
        const formatted =
          formatters.DATE_TIME(moment.utc(date), timezone, false) ||
          dayjs.utc(date).tz(getUserTimezone()).format('MMM DD, YYYY - h:mm A')
        return <Texto category='p2'>{formatted}</Texto>
      },
    },
    {
      headerName: 'Invoice BOL Date',
      width: 130,
      filter: createDateStatusDualColumnFilter({
        field: 'invoice_bol_date',
        dateFieldTitle: 'Filter By Invoice BOL Date',
        statusFieldTitle: 'Filter By Status',
        statusValueGetter: (params: ValueGetterParams<MatchInvoice>) => {
          const item = params.data
          if (!item?.invoice_bol_date) return 'Missing'
          return 'Has Date'
        },
      }),
      filterParams: createDateStatusDualColumnFilterParams({
        field: 'invoice_bol_date',
        dateFieldTitle: 'Filter By Invoice BOL Date',
        statusFieldTitle: 'Filter By Status',
        statusValueGetter: (params: ValueGetterParams<MatchInvoice>) => {
          const item = params.data
          if (!item?.invoice_bol_date) return 'Missing'
          return 'Has Date'
        },
      }),
      valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
        const item = params.data
        // BOL Invoice Date - this might be the same as invoice date or a separate field
        // From API structure, this could be invoice_bol_date or similar
        const date = item?.invoice_bol_date
        if (!date) return null
        // CRITICAL: Return the string directly, don't parse to Date here
        // Let the cellRenderer handle UTC parsing properly
        return date
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<MatchInvoice>) => {
        const item = params.data
        const date = item?.invoice_bol_date
        if (!date) return 'Missing'

        // Use timezone from match context or user timezone
        const formatted = formatters.DATE(moment.utc(date), timezone, false)
        return `${formatted || dayjs.utc(date).tz(getUserTimezone()).format('MMM DD, YYYY')} Has Date`
      },
      cellRenderer: (params: ICellRendererParams<MatchInvoice>) => {
        const date = params.value
        if (!date)
          return (
            <Texto category='p2' appearance='hint'>
              —
            </Texto>
          )

        // Parse as UTC (moment.utc handles both Z and non-Z timestamps correctly)
        const formatted =
          formatters.DATE_TIME(moment.utc(date), timezone, false) ||
          dayjs.utc(date).tz(getUserTimezone()).format('MMM DD, YYYY - h:mm A')
        return <Texto category='p2'>{formatted}</Texto>
      },
    },
    {
      headerName: 'BOL #',
      width: 150,
      filter: 'agSetColumnFilter',
      valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
        const bolNumbers = params.data?.bol_numbers
        if (!bolNumbers || bolNumbers.length === 0) return ''
        return bolNumbers.join(', ')
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<MatchInvoice>) => {
        const bolNumbers = params.data?.bol_numbers
        if (!bolNumbers || bolNumbers.length === 0) return ''
        return bolNumbers.join(' ')
      },
      cellRenderer: (params: ICellRendererParams<MatchInvoice>) => {
        const bolNumbers = params.data?.bol_numbers
        if (!bolNumbers || bolNumbers.length === 0) {
          return (
            <Texto category='p2' appearance='hint'>
              —
            </Texto>
          )
        }
        return (
          <Vertical gap={2}>
            {bolNumbers.map((bolNumber: string, index: number) => (
              <Texto key={`${bolNumber}-${index}`} category='p2'>
                {bolNumber}
              </Texto>
            ))}
          </Vertical>
        )
      },
    },
    {
      headerName: 'Supplier',
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
        const { supplier } = params.data ?? {}
        return supplier?.internal_name ?? supplier?.source_name ?? params.data?.supplier_name ?? 'Unknown'
      },
      cellRenderer: (params: ICellRendererParams<MatchInvoice>) => {
        const supplier = params.value
        return (
          <Texto className='text-ellipsis' category='p2'>
            {supplier}
          </Texto>
        )
      },
    },
    {
      headerName: 'Terminal',
      width: 180,
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
        const item = params.data as any
        // Original match items use terminal_name
        if (item?.original) {
          return item.terminal_name || 'N/A'
        }
        // Potential matches use terminal.internal_name || terminal.source_name
        return item?.terminal?.internal_name || item?.terminal?.source_name || 'N/A'
      },
      filterValueGetter: (params: ValueGetterParams<MatchInvoice>) => {
        const item = params.data as any
        if (item?.original) {
          return item.terminal_name || 'N/A'
        }
        return item?.terminal?.internal_name || item?.terminal?.source_name || 'N/A'
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<MatchInvoice>) => {
        const item = params.data as any
        if (item?.original) {
          return item.terminal_name || ''
        }
        return item?.terminal?.internal_name || item?.terminal?.source_name || ''
      },
      cellRenderer: (params: ICellRendererParams<MatchInvoice>) => {
        const terminal = params.value || 'N/A'
        return (
          <Texto className='text-ellipsis' category='p2'>
            {terminal}
          </Texto>
        )
      },
    },
    {
      headerName: 'Product(s)',
      flex: 1.2,
      autoHeight: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressSelectAll: false,
        suppressMiniFilter: false,
      },
      valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
        const details = (params.data as any)?.details || []
        if (!details || details.length === 0) return ''

        const productNamesSet = new Set<string>()
        details.forEach((detail: any) => {
          if (detail.product) {
            const productName = detail.product.internal_name || detail.product.source_name
            if (productName) {
              // For tax items, append "(Tax)" to distinguish them
              const displayName = detail.type === 'tax' ? `${productName} (Tax)` : productName
              productNamesSet.add(displayName)
            }
          }
        })
        return Array.from(productNamesSet).join(', ')
      },
      getQuickFilterText: (params: GetQuickFilterTextParams<MatchInvoice>) => {
        const details = (params.data as any)?.details || []
        if (!details || details.length === 0) return ''

        const productNamesSet = new Set<string>()
        details.forEach((detail: any) => {
          if (detail.product) {
            const productName = detail.product.internal_name || detail.product.source_name
            if (productName) {
              // For tax items, append "(Tax)" to distinguish them
              const displayName = detail.type === 'tax' ? `${productName} (Tax)` : productName
              productNamesSet.add(displayName)
            }
          }
        })
        return Array.from(productNamesSet).join(' ')
      },
      cellRenderer: ProductsCellRenderer,
    },
    {
      headerName: 'Net Volume',
      width: 120,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
        const item = params.data

        // API response has net_volume at the top level
        if (item?.net_volume != null) return item.net_volume

        // Fallback: sum from details array for supply items
        const details = item?.details || []
        let totalVolume = 0
        details.forEach((detail: any) => {
          if (detail.type !== 'tax' && detail.net_volume) {
            totalVolume += detail.net_volume || 0
          }
        })

        return totalVolume || 0
      },
      cellRenderer: (params: ICellRendererParams<MatchInvoice>) => {
        const volume = params.value
        if (volume == null || volume === 0)
          return (
            <Texto category='p2' appearance='hint'>
              —
            </Texto>
          )

        const formatted = formatVolumeConsistent(volume, columnPrecisions.volume)
        return (
          <Texto category='p2' align='right'>
            {formatted}
          </Texto>
        )
      },
    },
    {
      headerName: 'Gross Volume',
      width: 120,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
        const item = params.data

        // API response has gross_volume at the top level
        if (item?.gross_volume != null) return item.gross_volume

        // Fallback: sum from details array for supply items
        const details = item?.details || []
        let totalVolume = 0
        details.forEach((detail: any) => {
          if (detail.type !== 'tax' && detail.gross_volume) {
            totalVolume += detail.gross_volume || 0
          }
        })

        return totalVolume || 0
      },
      cellRenderer: (params: ICellRendererParams<MatchInvoice>) => {
        const volume = params.value
        if (volume == null || volume === 0)
          return (
            <Texto category='p2' appearance='hint'>
              —
            </Texto>
          )

        const formatted = formatVolumeConsistent(volume, columnPrecisions.volume)
        return (
          <Texto category='p2' align='right'>
            {formatted}
          </Texto>
        )
      },
    },
    {
      headerName: 'Product Total',
      width: 120,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
        // For invoice data, sum from details array
        if (params.data?.details) {
          // For pinned rows, use product_total if available (only for supply items)
          if (params.data._isPinnedMatchItem) {
            return params.data.details
              .filter((detail: { type?: string }) => detail.type !== 'tax')
              .reduce((sum: number, detail: { product_total?: number }) => sum + (detail.product_total || 0), 0)
          }
          // For regular API response, use total field (only for supply items)
          return params.data.details
            .filter((detail: { type?: string }) => detail.type !== 'tax')
            .reduce((sum: number, detail: { total?: number }) => sum + (detail.total || 0), 0)
        }
        return 0
      },
      cellRenderer: ProductTotalCurrencyRenderer,
    },
    {
      headerName: 'Tax Total',
      width: 110,
      type: 'numericColumn',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
        if (params.data?.details) {
          // Sum tax amounts from details array - handle both tax items and embedded tax_total
          return params.data.details.reduce(
            (sum: number, detail: { type?: string; total?: number; tax_total?: number }) => {
              if (detail.type === 'tax') {
                // For tax-only items, the total field contains the tax amount
                return sum + (detail.total || 0)
              } else if (detail.tax_total) {
                // For supply items with embedded tax, use tax_total field
                return sum + (detail.tax_total || 0)
              }
              return sum
            },
            0
          )
        }
        return 0
      },
      cellRenderer: TaxTotalCurrencyRenderer,
    },
    {
      headerName: 'Total',
      width: 120,
      type: 'numericColumn',
      pinned: 'right',
      filter: 'agNumberColumnFilter',
      filterParams: {
        buttons: ['reset'],
        suppressAndOrCondition: false,
      },
      valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
        // Calculate total as Product Total + Tax Total
        if (params.data?.details) {
          // Calculate product total (supply items only)
          const productTotal = params.data.details
            .filter((detail: { type?: string }) => detail.type !== 'tax')
            .reduce((sum: number, detail: { total?: number; product_total?: number }) => {
              // Use product_total if available, otherwise use total field
              const amount = detail.product_total || detail.total || 0
              return sum + amount
            }, 0)

          // Calculate tax total (tax items + embedded tax)
          const taxTotal = params.data.details.reduce(
            (sum: number, detail: { type?: string; total?: number; tax_total?: number }) => {
              if (detail.type === 'tax') {
                // For tax-only items, the total field contains the tax amount
                return sum + (detail.total || 0)
              } else if (detail.tax_total) {
                // For supply items with embedded tax, use tax_total field
                return sum + (detail.tax_total || 0)
              }
              return sum
            },
            0
          )

          return productTotal + taxTotal
        }
        // Fallback to invoice_total if details array is not available
        return params.data?.invoice_total || 0
      },
      cellRenderer: InvoiceTotalCurrencyRenderer,
    },
    {
      headerName: 'Actions',
      width: 120,
      pinned: 'right',
      sortable: false,
      resizable: false,
      suppressMenu: true,
      cellRenderer: (params: ICellRendererParams<MatchInvoice>) => {
        return ManageMatchActionRenderer({
          data: params.data,
          actions,
          helpers,
          searchType: 'invoice',
        })
      },
    },
  ]
}
